/**
 * APC Investment Landing Page — public route /esquemadeinversion
 *
 * Sections (mirrors the master requirements doc):
 *   1. Hero with primary CTA
 *   2. Quiénes Somos + Modelo de Negocio + Canales
 *   3. ¿Por qué APC? con métricas animadas (editables desde Ajustes)
 *   4. Tecnología propia
 *   5. Categorías y Marcas comercializadas
 *   6. Planes de Inversión (6m / 12m)
 *   7. Calculadora interactiva (USD + MXN)
 *   8. Uso del Capital (5 tarjetas)
 *   9. Preguntas Frecuentes (accordion)
 *  10. CTA final + Footer
 *
 * Modal de formulario de interés con dedupe en backend.
 *
 * Estilo: Corporativo / Tecnológico / Financiero / Premium.
 * Paleta: Azul oscuro #0B1F44, Blanco, Gris zinc, Verde #00873D (indicadores financieros).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion, useInView } from "framer-motion";
import {
  Buildings, ShoppingCart, Storefront, ChartLineUp, Cpu, Truck, GraduationCap,
  Users, Globe, Clock, Briefcase, Package, Wrench, Code, CurrencyDollar,
  CheckCircle, ArrowRight, X, WhatsappLogo,
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmtUSD = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtMXN = (n) => `MX$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

function AnimatedCounter({ value, suffix = "", duration = 1500 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(value * eased));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, value, duration]);
  return <span ref={ref}>{n.toLocaleString("es-MX")}{suffix}</span>;
}

export default function InvestmentLanding() {
  const [metrics, setMetrics] = useState({ clients: 1500, equipment_sold: 3000, states: 8, years: 5, exchange_rate: 17 });
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Esquema de Inversión — APC Tecnología";
    axios.get(`${API}/api/public/apc-metrics`).then((r) => setMetrics(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white text-zinc-900 antialiased">
      {/* ============ HERO ============ */}
      <header className="relative bg-[#0B1F44] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none"
             style={{ backgroundImage: "radial-gradient(ellipse at top, #1e3a8a 0%, transparent 60%)" }} />
        <nav className="relative max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <a href="/" className="font-heading font-extrabold text-xl tracking-tight hover:opacity-80 transition" data-testid="nav-logo-home">APC <span className="font-mono text-xs opacity-60 ml-1">TECNOLOGÍA</span></a>
          <a href="#contacto" className="hidden sm:inline-flex text-xs uppercase tracking-widest text-white/80 hover:text-white">Contacto</a>
        </nav>
        <div className="relative max-w-7xl mx-auto px-6 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#00873D]/40 bg-[#00873D]/10 text-[#3FD27E] text-xs font-mono uppercase tracking-widest mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#3FD27E] animate-pulse"/> Esquema de Inversión 2026
            </div>
            <h1 className="font-heading font-extrabold text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Invierte en el crecimiento de <span className="text-[#3FD27E]">APC</span>.
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-white/75 max-w-xl leading-relaxed">
              Forma parte del crecimiento de una empresa tecnológica mexicana con visión de expansión nacional. Rendimientos del <b className="text-white">25 % al 35 %</b> según plazo y monto.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <button
                onClick={() => setModalOpen(true)}
                className="inline-flex items-center gap-2 px-7 h-14 rounded-full bg-[#00873D] hover:bg-[#006e30] text-white font-semibold text-sm shadow-lg shadow-[#00873D]/30 transition-colors"
                data-testid="hero-cta"
              >
                ¿Quieres invertir con nosotros? <ArrowRight size={16} weight="bold" />
              </button>
              <a href="#calculadora" className="inline-flex items-center px-6 h-14 rounded-full border border-white/30 hover:border-white/60 text-white text-sm font-medium transition-colors">
                Simular inversión
              </a>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}
                      className="relative">
            <GrowthChart years={Math.max(10, metrics.years || 10)} />
          </motion.div>
        </div>
      </header>

      {/* ============ QUIÉNES SOMOS ============ */}
      <Section eyebrow="Quiénes Somos" title="Una empresa mexicana de tecnología con cobertura nacional.">
        <p className="text-lg text-zinc-600 leading-relaxed max-w-3xl">
          APC es una empresa especializada en la comercialización y distribución de tecnología a nivel nacional, atendiendo a empresas, escuelas, profesionistas y consumidores finales bajo modelo B2B y B2C.
        </p>
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {["Sitio web", "Tienda en línea", "Facebook", "Instagram", "TikTok", "WhatsApp", "Mercado Libre", "Ventas directas"].map((ch) => (
            <div key={ch} className="border border-zinc-200 bg-white rounded-xl px-4 py-3 text-sm text-zinc-700 hover:border-[#0B1F44] transition-colors">
              {ch}
            </div>
          ))}
        </div>
      </Section>

      {/* ============ ¿POR QUÉ APC? ============ */}
      <Section dark eyebrow="¿Por qué APC?" title="Modelo probado, crecimiento sostenido y operación digital.">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: ChartLineUp, t: "Experiencia comprobada", d: "Más de 5 años operando en tecnología, con resultados medibles." },
            { icon: Globe,       t: "Cobertura nacional",     d: "Atendemos múltiples estados de México y crecemos cada trimestre." },
            { icon: Storefront,  t: "Multicanal",             d: "Ventas físicas + digitales: web, marketplaces, redes sociales." },
            { icon: CheckCircle, t: "Modelo probado",         d: "Distribución validada con clientes recurrentes B2B y B2C." },
            { icon: Cpu,         t: "Operación digital",      d: "Software propio para inventario, CRM, finanzas y ventas." },
            { icon: Briefcase,   t: "Capacidad de expansión", d: "Listos para escalar a nuevas regiones y categorías." },
          ].map((c) => (
            <motion.div key={c.t}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4 }}
              className="rounded-2xl bg-white/5 border border-white/10 p-6 backdrop-blur hover:border-[#3FD27E]/40 transition-colors">
              <c.icon size={24} className="text-[#3FD27E] mb-4" />
              <div className="font-heading text-lg font-semibold mb-1">{c.t}</div>
              <div className="text-sm text-white/70 leading-relaxed">{c.d}</div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ============ TECNOLOGÍA ============ */}
      <Section eyebrow="Nuestra Tecnología" title="Software propio. Operación 100 % digital.">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <p className="text-lg text-zinc-600 leading-relaxed">
            APC opera con un ERP desarrollado a medida por nuestro equipo de ingeniería que gestiona clientes, inventario, finanzas, ventas y CRM. Esto nos permite automatizar procesos, controlar operaciones en tiempo real y escalar sin perder eficiencia.
          </p>
          <div className="space-y-3">
            {["Gestionar clientes y vendedores", "Controlar operaciones en tiempo real", "Gestionar inventario y stock", "Automatizar pedidos y cobros", "Escalar a múltiples sucursales"].map((it) => (
              <div key={it} className="flex items-start gap-3">
                <CheckCircle size={20} weight="fill" className="text-[#00873D] shrink-0 mt-0.5" />
                <span className="text-zinc-700">{it}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ============ CATEGORÍAS + MARCAS ============ */}
      <Section className="bg-zinc-50" eyebrow="Catálogo" title="Categorías y marcas líderes.">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-10">
          {[
            { i: Cpu,           l: "Laptops" },
            { i: ChartLineUp,   l: "Computadoras Gamer" },
            { i: Buildings,     l: "Workstations" },
            { i: Cpu,           l: "Celulares" },
            { i: Cpu,           l: "Monitores" },
            { i: Wrench,        l: "Componentes" },
            { i: Cpu,           l: "Impresoras" },
            { i: Package,       l: "Accesorios" },
            { i: Briefcase,     l: "Equipamiento empresarial" },
            { i: GraduationCap, l: "Tecnología educativa" },
          ].map((c) => (
            <div key={c.l} className="bg-white rounded-xl border border-zinc-200 p-4 text-sm flex items-center gap-3 hover:border-[#0B1F44] transition-colors">
              <c.i size={18} className="text-[#0B1F44]" />
              {c.l}
            </div>
          ))}
        </div>
        <div className="text-xs uppercase tracking-widest font-mono text-zinc-500 mb-3">Marcas comercializadas</div>
        <div className="flex flex-wrap gap-2">
          {["Dell", "HP", "Lenovo", "Asus", "Samsung", "LG", "Apple", "Microsoft", "Acer", "MSI", "Logitech", "Kingston"].map((b) => (
            <span key={b} className="px-3 py-1.5 rounded-full bg-white border border-zinc-200 text-sm">{b}</span>
          ))}
          <span className="px-3 py-1.5 rounded-full bg-zinc-100 text-sm text-zinc-500 italic">+ otras marcas líderes</span>
        </div>
      </Section>

      {/* ============ PLANES DE INVERSIÓN ============ */}
      <Section dark eyebrow="Planes" title="Dos planes diseñados para inversionistas con visión.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <PlanCard months={6}  min={1000}  max={10000} pct={25} highlight={false} onCTA={() => setModalOpen(true)} />
          <PlanCard months={12} min={20000} maxLabel="$50,000 a $100,000 USD" pct={35} pctLabel="% a negociar" highlight onCTA={() => setModalOpen(true)} />
        </div>
        <p className="text-center text-xs text-white/50 mt-6 italic">* Porcentajes referenciales y negociables según monto y plazo.</p>
      </Section>

      {/* ============ CALCULADORA ============ */}
      <CalculatorSection rate={metrics.exchange_rate} onApply={() => setModalOpen(true)} />

      {/* ============ USO DEL CAPITAL ============ */}      <Section className="bg-zinc-50" eyebrow="Uso del Capital" title="Tu inversión se traduce en crecimiento real.">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { i: Package,   t: "Inventario",      d: "Compra de mercancía estratégica." },
            { i: Buildings, t: "Expansión",       d: "Nuevas sucursales y puntos de venta." },
            { i: Users,     t: "Capital Humano",  d: "Contratación de talento clave." },
            { i: Code,      t: "Tecnología",      d: "Desarrollo continuo de software propio." },
            { i: Truck,     t: "Infraestructura", d: "Herramientas, logística y equipamiento." },
          ].map((c) => (
            <div key={c.t} className="bg-white rounded-2xl border border-zinc-200 p-6">
              <c.i size={24} className="text-[#00873D] mb-3" />
              <div className="font-heading text-base font-bold">{c.t}</div>
              <div className="text-xs text-zinc-600 mt-1 leading-relaxed">{c.d}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* ============ FINAL CTA ============ */}
      <section id="contacto" className="bg-[#0B1F44] text-white text-center py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">¿Listo para invertir con nosotros?</h2>
          <p className="mt-4 text-white/70 text-lg">Déjanos tus datos y un asesor APC te contactará con información personalizada.</p>
          <button
            onClick={() => setModalOpen(true)}
            className="mt-10 inline-flex items-center gap-2 px-8 h-14 rounded-full bg-[#00873D] hover:bg-[#006e30] text-white font-semibold shadow-lg shadow-[#00873D]/30 transition-colors"
            data-testid="final-cta"
          >
            ¿Quieres invertir con nosotros? <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </section>

      <footer className="bg-[#06122B] text-white/50 py-8 text-center text-xs">
        <div className="space-y-3">
          <div>© {new Date().getFullYear()} APC Tecnología</div>
          <div className="flex items-center justify-center gap-4 text-[10px] uppercase tracking-widest font-mono">
            <a href="/esquemadeinversion" className="text-white/40 hover:text-[#3FD27E] transition-colors" data-testid="footer-link-invest">Invierte</a>
            <span className="text-white/20">·</span>
            <a href="/esquemadecomisiones" className="text-white/40 hover:text-[#3FD27E] transition-colors" data-testid="footer-link-connect">Conéctanos</a>
          </div>
        </div>
      </footer>

      {modalOpen && <LeadModal onClose={() => setModalOpen(false)} />}
    </div>
  );
}

// ====================================================================== //

function Section({ children, eyebrow, title, dark = false, className = "" }) {
  return (
    <section className={`${dark ? "bg-[#0B1F44] text-white" : ""} ${className}`}>
      <div className="max-w-7xl mx-auto px-6 py-20">
        {eyebrow && (
          <div className={`text-xs font-mono uppercase tracking-[0.25em] mb-3 ${dark ? "text-[#3FD27E]" : "text-[#00873D]"}`}>{eyebrow}</div>
        )}
        {title && (
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5 }}
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-10 max-w-3xl">
            {title}
          </motion.h2>
        )}
        {children}
      </div>
    </section>
  );
}

function GrowthChart({ years = 10 }) {
  // Smooth exponential-ish growth path. Width 400, height 240.
  // Animates via stroke-dashoffset on mount.
  const path = "M 10 220 C 60 215, 110 200, 140 180 S 200 160, 230 130 S 290 95, 320 65 S 360 30, 390 18";
  return (
    <div className="relative rounded-3xl bg-white/5 backdrop-blur border border-white/10 p-6 sm:p-8 overflow-hidden">
      <div className="absolute inset-0 opacity-30 pointer-events-none"
           style={{ backgroundImage: "radial-gradient(ellipse at bottom right, #00873D 0%, transparent 60%)" }} />
      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-[#3FD27E]">Trayectoria APC</div>
          <div className="text-[10px] font-mono text-white/40">2014 → 2026</div>
        </div>
        <svg viewBox="0 0 400 240" className="w-full h-auto" preserveAspectRatio="none">
          {/* Grid */}
          <g stroke="rgba(255,255,255,0.06)" strokeWidth="1">
            {[40, 80, 120, 160, 200].map((y) => <line key={y} x1="0" x2="400" y1={y} y2={y} />)}
          </g>
          {/* Area fill */}
          <defs>
            <linearGradient id="area" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#3FD27E" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#3FD27E" stopOpacity="0" />
            </linearGradient>
          </defs>
          <motion.path
            d={`${path} L 390 230 L 10 230 Z`}
            fill="url(#area)"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          />
          {/* Animated line */}
          <motion.path
            d={path}
            fill="none"
            stroke="#3FD27E"
            strokeWidth="3"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 2, ease: "easeOut" }}
          />
          {/* Endpoint pulse */}
          <motion.circle cx="390" cy="18" r="6" fill="#3FD27E"
            initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1.8, duration: 0.4 }} />
          <motion.circle cx="390" cy="18" r="12" fill="none" stroke="#3FD27E" strokeWidth="2"
            initial={{ opacity: 0.6, scale: 0.8 }}
            animate={{ opacity: 0, scale: 2 }}
            transition={{ delay: 2, duration: 1.5, repeat: Infinity }} />
        </svg>
        <div className="mt-6 pt-4 border-t border-white/10 flex items-end justify-between gap-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-white/50">Experiencia</div>
            <div className="font-heading text-5xl font-extrabold leading-none mt-1">
              +<AnimatedCounter value={years} /> <span className="text-2xl text-white/70 font-bold">años</span>
            </div>
          </div>
          <div className="text-right text-xs text-[#3FD27E]/80 font-mono uppercase tracking-widest">
            Crecimiento sostenido
          </div>
        </div>
      </div>
    </div>
  );
}


function PlanCard({ months, min, max, maxLabel, pct, pctLabel, highlight, onCTA }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={`relative rounded-3xl p-8 ${highlight ? "bg-[#00873D] text-white" : "bg-white/5 backdrop-blur border border-white/10 text-white"}`}>
      {highlight && (
        <span className="absolute -top-3 right-6 px-3 py-1 rounded-full bg-white text-[#00873D] text-[10px] font-bold uppercase tracking-widest">Más solicitado</span>
      )}
      <div className={`text-xs font-mono uppercase tracking-widest ${highlight ? "text-white/80" : "text-[#3FD27E]"}`}>Plan {months} meses</div>
      <div className="font-heading text-5xl font-extrabold mt-2">{pct}%{pctLabel ? "+" : ""}</div>
      <div className={`text-sm ${highlight ? "text-white/80" : "text-white/60"}`}>{pctLabel || "de retorno sugerido"}</div>
      <div className="mt-6 pt-6 border-t border-white/15 space-y-2 text-sm">
        <div className="flex justify-between"><span className={highlight ? "text-white/70" : "text-white/60"}>Inversión mínima</span><b>{fmtUSD(min)}</b></div>
        <div className="flex justify-between gap-3">
          <span className={highlight ? "text-white/70" : "text-white/60"}>{maxLabel ? "Inversión mayor" : "Inversión máxima"}</span>
          <b className="text-right">{maxLabel ? maxLabel : fmtUSD(max)}</b>
        </div>
        <div className="flex justify-between"><span className={highlight ? "text-white/70" : "text-white/60"}>Plazo</span><b>{months} meses</b></div>
      </div>
      <button
        onClick={onCTA}
        data-testid={`plan-cta-${months}`}
        className={`mt-8 w-full h-12 rounded-full font-semibold text-sm transition-colors ${highlight ? "bg-white text-[#00873D] hover:bg-zinc-100" : "bg-[#00873D] hover:bg-[#006e30] text-white"}`}>
        Aplicar a este plan
      </button>
    </motion.div>
  );
}

function CalculatorSection({ rate, onApply }) {
  const [currency, setCurrency] = useState("USD");
  const [amount, setAmount] = useState(10000);
  const [term, setTerm] = useState(6);
  const [customTerm, setCustomTerm] = useState(9);
  const [pct, setPct] = useState(25);

  const months = term === "custom" ? Math.max(1, Number(customTerm) || 1) : term;
  // Available % presets depend on the term chosen.
  const pctOptions = term === 6 ? [25, 30] : term === 12 ? [35, 40] : [25, 30, 35, 40];
  // If the previously selected pct is not in the current preset list, snap it
  // to the first option for clarity (user can still type a custom value).
  useEffect(() => {
    if (!pctOptions.includes(Number(pct))) setPct(pctOptions[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  const principal = Number(amount) || 0;
  const profit = principal * (Number(pct) || 0) / 100;
  const total = principal + profit;
  const dueDate = useMemo(() => {
    const d = new Date(); d.setMonth(d.getMonth() + months);
    return d.toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" });
  }, [months]);

  const showConv = currency === "USD"
    ? { label: "Equivalente en MXN", v: fmtMXN(total * rate) }
    : { label: "Equivalente en USD", v: fmtUSD(total / rate) };

  return (
    <section id="calculadora" className="bg-zinc-50">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-[#00873D] mb-3">Calculadora</div>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-10 max-w-3xl">Simula tu inversión.</h2>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Inputs */}
          <div className="lg:col-span-3 bg-white rounded-3xl border border-zinc-200 p-8 space-y-6">
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Moneda</label>
              <div className="inline-flex rounded-full bg-zinc-100 p-1 mt-1 ml-3 text-xs font-semibold">
                {["USD", "MXN"].map((c) => (
                  <button key={c} type="button" onClick={() => setCurrency(c)}
                          className={`px-4 py-1.5 rounded-full transition-colors ${currency === c ? "bg-white text-[#0B1F44] shadow-sm" : "text-zinc-500"}`}
                          data-testid={`calc-cur-${c}`}>{c}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Monto a invertir ({currency})</label>
              <input type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)}
                     className="mt-1 w-full text-3xl font-heading font-extrabold border-b-2 border-zinc-200 focus:border-[#00873D] outline-none py-2 numeric"
                     data-testid="calc-amount" />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Plazo</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {[6, 12, "custom"].map((t) => (
                  <button key={t} type="button" onClick={() => setTerm(t)}
                          className={`px-5 h-10 rounded-full border text-sm font-medium ${term === t ? "bg-[#0B1F44] text-white border-[#0B1F44]" : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-500"}`}
                          data-testid={`calc-term-${t}`}>
                    {t === "custom" ? "Personalizado" : `${t} meses`}
                  </button>
                ))}
                {term === "custom" && (
                  <input type="number" min={1} value={customTerm} onChange={(e) => setCustomTerm(e.target.value)}
                         className="w-24 h-10 px-3 rounded-full border border-zinc-300 text-sm" placeholder="meses" />
                )}
              </div>
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Porcentaje de retorno</label>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {pctOptions.map((p) => (
                  <button key={p} type="button" onClick={() => setPct(p)}
                          className={`w-12 h-10 rounded-full border text-sm font-semibold ${Number(pct) === p ? "bg-[#00873D] text-white border-[#00873D]" : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-500"}`}
                          data-testid={`calc-pct-${p}`}>{p}%</button>
                ))}
                <input type="number" value={pct} onChange={(e) => setPct(e.target.value)}
                       className="w-24 h-10 px-3 rounded-full border border-zinc-300 text-sm" placeholder="%" />
              </div>
            </div>
          </div>
          {/* Results */}
          <div className="lg:col-span-2 bg-[#0B1F44] text-white rounded-3xl p-8 space-y-4">
            <div className="text-xs font-mono uppercase tracking-widest text-[#3FD27E]">Resultado simulado</div>
            <Row label="Capital invertido"  value={currency === "USD" ? fmtUSD(principal) : fmtMXN(principal)} />
            <Row label="Porcentaje"          value={`${pct}%`} />
            <Row label="Plazo"               value={`${months} ${months === 1 ? "mes" : "meses"}`} />
            <Row label="Vencimiento"         value={dueDate} />
            <div className="pt-4 border-t border-white/15">
              <div className="text-xs font-mono uppercase tracking-widest text-white/60">Ganancia estimada</div>
              <div className="font-heading text-3xl font-extrabold text-[#3FD27E] numeric" data-testid="calc-profit">
                {currency === "USD" ? fmtUSD(profit) : fmtMXN(profit)}
              </div>
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-white/60">Total a recibir</div>
              <div className="font-heading text-4xl font-extrabold numeric" data-testid="calc-total">
                {currency === "USD" ? fmtUSD(total) : fmtMXN(total)}
              </div>
              <div className="text-[11px] text-white/50 mt-1">{showConv.label}: <b className="text-white/80">{showConv.v}</b></div>
            </div>
            <button
              onClick={onApply}
              data-testid="calc-cta"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[#00873D] hover:bg-[#006e30] text-white font-semibold text-sm transition-colors">
              Quiero esta inversión <ArrowRight size={14} weight="bold" />
            </button>
            <div className="text-[10px] text-white/40 italic">Simulación referencial. Sujeta a evaluación y formalización con APC.</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs uppercase tracking-widest font-mono text-white/60">{label}</span>
      <span className="font-semibold text-sm">{value}</span>
    </div>
  );
}

function FAQ_REMOVED() { return null; }
// (FAQ section removed by request — info now lives with the sales rep)


function LeadModal({ onClose }) {
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", notes_from_visitor: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    const f = { ...form };
    if (!f.full_name.trim() || !f.phone.trim() || !f.email.trim()) {
      setError("Por favor completa todos los campos.");
      return;
    }
    setLoading(true); setError("");
    try {
      await axios.post(`${API}/api/public/investors/leads`, f);
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "No pudimos enviar tu solicitud. Inténtalo de nuevo.");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="lead-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden">
        <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-zinc-100 inline-flex items-center justify-center z-10" data-testid="lead-modal-close" aria-label="Cerrar">
          <X size={18} />
        </button>
        {!sent ? (
          <form onSubmit={submit} className="p-8">
            <div className="text-xs font-mono uppercase tracking-widest text-[#00873D] mb-2">Formulario de interés</div>
            <h3 className="font-heading text-2xl font-extrabold mb-1">Recibe información personalizada</h3>
            <p className="text-sm text-zinc-500 mb-6">Un asesor de APC se pondrá en contacto contigo.</p>
            <div className="space-y-3">
              <input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Nombre completo *"
                     className="w-full h-12 px-4 border border-zinc-300 rounded-full focus:border-[#0B1F44] outline-none text-sm" data-testid="lead-name" />
              <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Celular *" type="tel"
                     className="w-full h-12 px-4 border border-zinc-300 rounded-full focus:border-[#0B1F44] outline-none text-sm" data-testid="lead-phone" />
              <input required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Correo electrónico *" type="email"
                     className="w-full h-12 px-4 border border-zinc-300 rounded-full focus:border-[#0B1F44] outline-none text-sm" data-testid="lead-email" />
              <textarea value={form.notes_from_visitor} onChange={(e) => setForm({ ...form, notes_from_visitor: e.target.value })} placeholder="¿Algún comentario? (opcional)" rows={2}
                        className="w-full px-4 py-3 border border-zinc-300 rounded-2xl focus:border-[#0B1F44] outline-none text-sm" data-testid="lead-notes" />
            </div>
            {error && <div className="mt-3 text-sm text-[#E0000F]">{error}</div>}
            <button type="submit" disabled={loading}
                    className="mt-6 w-full h-12 rounded-full bg-[#00873D] hover:bg-[#006e30] text-white font-semibold text-sm transition-colors disabled:opacity-60"
                    data-testid="lead-submit">
              {loading ? "Enviando…" : "Enviar solicitud"}
            </button>
            <p className="mt-3 text-[10px] text-zinc-400 text-center">Al enviar aceptas que un asesor te contacte. No compartimos tus datos.</p>
          </form>
        ) : (
          <div className="p-10 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#00873D]/10 flex items-center justify-center text-[#00873D] mb-4">
              <CheckCircle size={32} weight="fill" />
            </div>
            <h3 className="font-heading text-2xl font-extrabold mb-1">Gracias por tu interés en APC.</h3>
            <p className="text-sm text-zinc-600 mt-3">Uno de nuestros asesores se pondrá en contacto contigo para brindarte información personalizada sobre nuestro esquema de inversión.</p>
            <button onClick={onClose} className="mt-8 px-6 h-11 rounded-full bg-[#0B1F44] hover:bg-[#0A1A38] text-white font-semibold text-sm" data-testid="lead-success-close">Cerrar</button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
